import React, { useState, useCallback, useEffect } from 'react'
import {
  Drawer,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
} from '@mui/material'
import { Image } from 'react-bootstrap'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import DragAndDrop from '@/components/UI/DragAndDrop/DragAndDrop'
import { getAgentFromStorage } from '@/utils/appUtils'
import BasicModal from '@/components/UI/Dialog/Dialog'
import TipBox from '@/components/UI/TipBox'
import { HelperService } from '@/services/helper.service'
import RoleplayMediaService from '@/services/RoleplayMediaService'
import { cn } from '@/lib/utils'
import closeimg from '@/assets/New-images/close-icon-red.svg'
import pngIcon from '@/assets/images/file-png.svg'
import jpegIcon from '@/assets/images/file-jpeg.svg'
import gifIcon from '@/assets/images/file-gif.svg'
import webpIcon from '@/assets/images/file-webp.svg'

const helperService = new HelperService()
const roleplayMediaService = new RoleplayMediaService()

const getFileIcon = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || ''
  switch (extension) {
    case 'png':
      return pngIcon
    case 'jpg':
    case 'jpeg':
      return jpegIcon
    case 'gif':
      return gifIcon
    case 'webp':
      return webpIcon
    default:
      // Default to PNG icon for other image types
      return pngIcon
  }
}

interface ImageDrawerProps {
  open: boolean
  onClose: () => void
  onSave: (imageData: {
    file?: File
    id?: string
    ids?: string[]
    altText?: string
    fileNames?: string[] // Array of file names for multiple uploads
  }) => void
  onRemoveId?: (id: string) => void // Callback to remove ID from image
  onDelete?: () => void // Callback to delete entire image from editor
  initialData?: {
    id?: string
    ids?: string[]
    altText?: string
    url?: string // Add URL support
  }
}

const ImageDrawer: React.FC<ImageDrawerProps> = ({
  open,
  onClose,
  onSave,
  onRemoveId,
  onDelete,
  initialData,
}) => {
  const { t } = useTranslation('translation')
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imageUrl, setImageUrl] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [badgeLabel, setBadgeLabel] = useState<string>('') // Display name for the image
  // State to manage current IDs when editing
  const [currentIds, setCurrentIds] = useState<string[]>([])
  // State to store fetched media info for each ID
  const [mediaInfoMap, setMediaInfoMap] = useState<
    Record<
      string,
      {
        file_name: string
        file_size: number
        mime_type: string
        url: string
      }
    >
  >({})
  const [loadingMediaInfo, setLoadingMediaInfo] = useState(false)
  // State for replacing existing image
  const [replacingId, setReplacingId] = useState<string | null>(null)
  const [replacementFile, setReplacementFile] = useState<File | null>(null)
  const [replacementPreview, setReplacementPreview] = useState<string | null>(
    null,
  )
  const [isDirty, setIsDirty] = useState(false) // Track if user has modified list in Edit mode

  const isEditMode = !!(
    initialData?.ids?.length ||
    initialData?.id ||
    initialData?.url
  )

  // Load initial data when drawer opens
  useEffect(() => {
    if (open && initialData) {
      // Set display name from initialData
      setBadgeLabel(initialData.altText || '')

      if (initialData.ids && initialData.ids.length > 0) {
        // Multiple IDs
        setCurrentIds(initialData.ids)
        setImageUrl('')
        setImageFiles([])

        // Pre-populate if URL provided (bypass API call for display)
        if (initialData.url && initialData.ids.length === 1) {
          const id = initialData.ids[0]
          setMediaInfoMap({
            [id]: {
              url: initialData.url,
              file_name: id,
              file_size: 0,
              mime_type: 'image/png', // Default
            },
          })
        }
      } else if (initialData.id) {
        // Single ID
        setCurrentIds([initialData.id])
        setImageUrl('')
        setImageFiles([])

        // Pre-populate if URL provided
        if (initialData.url) {
          setMediaInfoMap({
            [initialData.id]: {
              url: initialData.url,
              file_name: initialData.id,
              file_size: 0,
              mime_type: 'image/png', // Default
            },
          })
        }
      } else {
        setCurrentIds([])
        setImageUrl('')
        setImageFiles([])
      }
      // Reset replacement states
      setReplacingId(null)
      setReplacementFile(null)
      setReplacementPreview(null)
      setIsDirty(false)
    } else if (open && !initialData) {
      // Reset when opening for new image
      setBadgeLabel('')
      setCurrentIds([])
      setImageUrl('')
      setImageFiles([])
      setMediaInfoMap({})
      setReplacingId(null)
      setReplacementFile(null)
      setReplacementPreview(null)
      setIsDirty(false)
    }
  }, [open, initialData])

  // Fetch media info for current IDs
  useEffect(() => {
    const fetchMediaInfo = async () => {
      if (currentIds.length === 0) {
        setMediaInfoMap({})
        return
      }

      setLoadingMediaInfo(true)
      const newMediaInfoMap: typeof mediaInfoMap = {}

      for (const id of currentIds) {
        // Skip if already fetched
        if (mediaInfoMap[id]) {
          newMediaInfoMap[id] = mediaInfoMap[id]
          continue
        }

        try {
          const response = await roleplayMediaService.getMediaInfo(id)
          // Try both possible response structures
          const media =
            response?.data?.data?.media || response?.data?.media || null

          if (media) {
            newMediaInfoMap[id] = {
              file_name: media.file_name || id,
              file_size: media.file_size || 0,
              mime_type: media.mime_type || 'image/png',
              // Use API URL if available, otherwise construct stream URL
              url: media.url || roleplayMediaService.getStreamUrl(id),
            }
          } else {
            // Fallback if API fails - use stream URL
            newMediaInfoMap[id] = {
              file_name: id,
              file_size: 0,
              mime_type: 'image/png',
              url: roleplayMediaService.getStreamUrl(id),
            }
          }
        } catch (error) {
          // Fallback on error - use stream URL
          newMediaInfoMap[id] = {
            file_name: id,
            file_size: 0,
            mime_type: 'image/png',
            url: roleplayMediaService.getStreamUrl(id),
          }
        }
      }

      setMediaInfoMap(newMediaInfoMap)
      setLoadingMediaInfo(false)
    }

    fetchMediaInfo()
  }, [currentIds])

  const tipMessage = t(
    'Tip: Make sure to specify when this image would be shown for the best experience',
  )

  const handleFileDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const imageFiles = files.filter(file => file.type.startsWith('image/'))

      if (imageFiles.length === 0) {
        helperService.showToaster(
          t('The file type you are trying to upload is not supported.'),
          'error',
        )
        return
      }

      // In Edit Mode, first addition replaces existing content
      if (isEditMode && !isDirty) {
        setCurrentIds([]) // Clear existing IDs
        setImageFiles(imageFiles)
        setIsDirty(true)
      } else {
        setImageFiles(prev => [...prev, ...imageFiles])
      }
    },
    [t, isEditMode, isDirty],
  )

  const handleRemoveImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleRemoveCurrentId = (idToRemove: string) => {
    const newIds = currentIds.filter(id => id !== idToRemove)
    setCurrentIds(newIds)

    // Update image immediately by calling onRemoveId callback
    if (onRemoveId) {
      onRemoveId(idToRemove)
    }
  }

  // Handler to start replacing an existing image
  const handleStartReplace = (imageId: string) => {
    setReplacingId(imageId)
    setReplacementFile(null)
    setReplacementPreview(null)
  }

  // Handler for replacement file drop
  const handleReplacementFileDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const imageFile = files.find(file => file.type.startsWith('image/'))
      if (!imageFile) {
        helperService.showToaster(
          t('The file type you are trying to upload is not supported.'),
          'error',
        )
        return
      }

      setReplacementFile(imageFile)
      // Create preview
      const reader = new FileReader()
      reader.onload = e => {
        setReplacementPreview(e.target?.result as string)
      }
      reader.readAsDataURL(imageFile)
    },
    [t],
  )

  // Handler to cancel replacement
  const handleCancelReplacement = () => {
    setReplacingId(null)
    setReplacementFile(null)
    setReplacementPreview(null)
  }

  // Handler to confirm replacement and call updateMedia API
  const handleConfirmReplacement = async () => {
    if (!replacingId || !replacementFile) return

    try {
      setSaving(true)
      const formData = new FormData()
      formData.append('file', replacementFile)
      formData.append('filepath', replacementFile.name)

      const response = await roleplayMediaService.updateMedia(
        replacingId,
        formData,
      )
      const responseData = response?.data
      const status = responseData?.status
      let statusCode: number | undefined

      if (status != null && status !== undefined) {
        if (typeof status === 'object') {
          const statusObj = status as { code?: number } | null
          if (
            statusObj &&
            'code' in statusObj &&
            typeof statusObj.code === 'number'
          ) {
            statusCode = statusObj.code
          }
        } else if (typeof status === 'number') {
          statusCode = status
        }
      }

      if (statusCode === 200) {
        helperService.showToaster(t('Image updated successfully'), 'success')
        // Update mediaInfoMap with new info
        const media = responseData?.data?.media
        if (media) {
          setMediaInfoMap(prev => ({
            ...prev,
            [replacingId]: {
              file_name: media.file_name || replacementFile.name,
              file_size: media.file_size || replacementFile.size,
              mime_type: media.mime_type || replacementFile.type,
              url: media.url || replacementPreview || '',
            },
          }))
        }
        // Reset replacement state
        setReplacingId(null)
        setReplacementFile(null)
        setReplacementPreview(null)
      } else {
        throw new Error(responseData?.message || t('Failed to update image'))
      }
    } catch (error: any) {
      helperService.showToaster(
        error?.message || t('Failed to update image'),
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const handleSave = async () => {
    // If editing with IDs, allow saving IDs directly
    if ((currentIds.length > 0 || imageUrl) && imageFiles.length === 0) {
      if (currentIds.length > 0) {
        // Get file names from mediaInfoMap if available
        const fileNames = currentIds
          .map(id => mediaInfoMap[id]?.file_name)
          .filter(Boolean) as string[]
        // Use badgeLabel if provided, otherwise use first file name, or fallback to first ID
        const altText = badgeLabel.trim() || fileNames[0] || currentIds[0]
        await onSave({
          ids: currentIds,
          id: currentIds[0],
          altText,
          fileNames: fileNames.length > 0 ? fileNames : undefined,
        })
      } else {
        // For URL - extract name from URL or use badgeLabel
        const urlName = imageUrl
          ? new URL(imageUrl).pathname.split('/').pop() || imageUrl
          : ''
        const altText = badgeLabel.trim() || urlName || imageUrl
        await onSave({ id: imageUrl, altText })
      }
      setImageUrl('')
      onClose()
      return
    }

    if (imageFiles.length === 0) {
      helperService.showToaster(t('Please upload an image'), 'error')
      return
    }

    try {
      setSaving(true)

      // Upload file to server
      const formData = new FormData()
      formData.append('file', imageFiles[0])
      formData.append('filepath', imageFiles[0].name)

      const response = await roleplayMediaService.uploadImage(formData)

      let statusCode: number | undefined
      let statusMessage: string | null = null

      const responseData = response?.data
      const status = responseData?.status
      if (status != null && status !== undefined) {
        if (typeof status === 'object') {
          // TypeScript guard: after typeof check, status is object | null, so check null again
          const statusObj = status as {
            code?: number
            message?: string | null
          } | null
          if (
            statusObj &&
            'code' in statusObj &&
            typeof statusObj.code === 'number'
          ) {
            statusCode = statusObj.code
            statusMessage = statusObj.message || null
          }
        } else if (typeof status === 'number') {
          statusCode = status
        }
      }

      if (statusCode === 200 && responseData?.data?.media?._id) {
        const imageId = responseData.data.media._id
        // If multiple files uploaded, collect all IDs and file names
        if (imageFiles.length > 1) {
          // Upload remaining files
          const remainingFiles = imageFiles.slice(1)
          const remainingIds: string[] = [imageId]
          const fileNames: string[] = [imageFiles[0].name] // Store file names

          for (const file of remainingFiles) {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('filepath', file.name)

            const response = await roleplayMediaService.uploadImage(formData)
            const responseData = response?.data
            const status = responseData?.status
            let statusCode: number | undefined

            if (status != null && status !== undefined) {
              if (typeof status === 'object') {
                const statusObj = status as {
                  code?: number
                  message?: string | null
                } | null
                if (
                  statusObj &&
                  'code' in statusObj &&
                  typeof statusObj.code === 'number'
                ) {
                  statusCode = statusObj.code
                }
              } else if (typeof status === 'number') {
                statusCode = status
              }
            }

            if (statusCode === 200 && responseData?.data?.media?._id) {
              remainingIds.push(responseData.data.media._id)
              fileNames.push(file.name)
            }
          }

          // Use badgeLabel if provided, otherwise use first file name
          const altText = badgeLabel.trim() || fileNames[0]
          await onSave({
            ids: remainingIds,
            altText,
            file: imageFiles[0],
            fileNames: fileNames,
          })
        } else {
          // Single file - use badgeLabel if provided, otherwise use file name
          const altText = badgeLabel.trim() || imageFiles[0].name
          await onSave({ id: imageId, altText, file: imageFiles[0] })
        }
      } else {
        throw new Error(
          statusMessage || responseData?.message || t('Failed to upload image'),
        )
      }

      // Reset state
      setImageFiles([])
      setImageUrl('')
      onClose()
    } catch (error: any) {
      helperService.showToaster(
        error?.message || t('Failed to save image'),
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (!saving) {
      setImageFiles([])
      onClose()
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 420, md: 503 },
          maxWidth: '100%',
          boxSizing: 'border-box',
          background: '#FFFFFF',
          border: '1px solid #ECEFF7',
          borderRadius: '6px',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            pb: 2,
            borderBottom: '1px solid #ECEFF7',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {isEditMode ? t('Edit Image') : t('Upload Image')}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack spacing={2}>
            {/* Tip Box Display */}
            <TipBox message={tipMessage} />

            {/* Name Input - Only show in edit mode */}
            {isEditMode && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('Name')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t(
                    'Enter a name for this image (e.g., "Scenario Image", "Example")',
                  )}
                  value={badgeLabel}
                  onChange={e => setBadgeLabel(e.target.value)}
                  disabled={saving}
                  helperText={t(
                    'This name will be shown in the editor to identify this image',
                  )}
                />
              </Box>
            )}

            {/* Upload Area - Only show when NOT editing existing images */}
            {currentIds.length === 0 && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  minHeight: { xs: 200, sm: 220, md: 240 },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    '& .custom-file-wrap': {
                      width: '100% !important',
                      height: '100% !important',
                      display: 'flex !important',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '180px',
                    },
                  }}
                >
                  <DragAndDrop
                    getFileDocumentsHandler={handleFileDrop}
                    supportedTypes=".png, .jpg, .jpeg, .gif, .webp"
                    maxFiles={100}
                    message={`<h2 className='mb-1'>${t('Drop your files here or click to browse.')}</h2><div>${t('Supported File types: .png .gif .png')}</div>`}
                    className={cn(
                      saving ? 'tw-opacity-50 tw-pointer-events-none' : '',
                      'tw-w-full',
                    )}
                  />
                </Box>
              </Box>
            )}

            {/* Selected Files List - Only when not editing */}
            {currentIds.length === 0 && imageFiles.length > 0 && (
              <div className="selected-files mt-3 w-100">
                {imageFiles.map((file, index) => (
                  <div
                    key={index}
                    className="files d-flex align-items-center border-grey radius-4 px-3 py-2 mb-2"
                  >
                    <div className="d-flex align-items-center tw-w-full">
                      <Image src={getFileIcon(file.name)} />
                      <div className="ml-3 mr-5 font-sm truncate-document tw-flex-1">
                        <strong>{file.name}</strong>
                        <span className="font-12 clr-black font-600 tw-block">
                          {formatBytes(file.size)}
                        </span>
                      </div>
                      <div className="ml-auto w-auto d-flex align-items-center">
                        {!saving && (
                          <button
                            className="btn p-0 ml-3 btn-red-close"
                            onClick={() => handleRemoveImage(index)}
                          >
                            <Image src={closeimg} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Current Images (when editing) - Simple pattern like ImageEditDrawer */}
            {currentIds.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('Preview Image')}
                </Typography>
                {loadingMediaInfo ? (
                  <Box className="tw-text-center tw-py-4 tw-text-gray-500">
                    {t('Loading...')}
                  </Box>
                ) : (
                  currentIds.map((imageId, index) => {
                    const mediaInfo = mediaInfoMap[imageId]
                    const fileName = mediaInfo?.file_name || imageId
                    const fileSize = mediaInfo?.file_size || 0
                    const imageUrl = mediaInfo?.url || ''
                    const isDeleted = replacingId === imageId // Use replacingId as "deleted" flag

                    return (
                      <Box
                        key={index}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: 220,
                          mb: 2,
                        }}
                      >
                        {/* Show DragAndDrop if no image or deleted, AND no replacement preview */}
                        {(!imageUrl || isDeleted) && !replacementPreview && (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              '& .custom-file-wrap': {
                                width: '100% !important',
                                height: '100% !important',
                                display: 'flex !important',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '180px',
                              },
                            }}
                          >
                            <DragAndDrop
                              getFileDocumentsHandler={
                                handleReplacementFileDrop
                              }
                              supportedTypes=".png, .jpg, .jpeg, .gif, .webp"
                              maxFiles={1}
                              message={`<h2 class='mb-1'>${t('Drop your image here or click to browse.')}</h2><div>${t('Supported formats: PNG, JPG, JPEG, GIF, WEBP')}</div>`}
                              className={cn(
                                saving
                                  ? 'tw-opacity-50 tw-pointer-events-none'
                                  : '',
                                'tw-w-full',
                              )}
                            />
                          </Box>
                        )}

                        {/* Show image preview with X button if image exists and not deleted */}
                        {imageUrl && !isDeleted && !replacementPreview && (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                            }}
                          >
                            <img
                              src={imageUrl}
                              alt={fileName}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block',
                              }}
                            />
                            <IconButton
                              onClick={() => setReplacingId(imageId)}
                              disabled={saving}
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: '#FFFFFF',
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                  backgroundColor: '#FFFFFF',
                                  transform: 'scale(1.05)',
                                },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 20, color: '#666' }} />
                            </IconButton>
                          </Box>
                        )}

                        {/* Show new image preview if uploaded */}
                        {replacementPreview && (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              border: '2px solid',
                              borderColor: 'primary.main',
                            }}
                          >
                            <img
                              src={replacementPreview}
                              alt="New image"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block',
                              }}
                            />
                            <IconButton
                              onClick={handleCancelReplacement}
                              disabled={saving}
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: '#FFFFFF',
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                  backgroundColor: '#FFFFFF',
                                  transform: 'scale(1.05)',
                                },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 20, color: '#666' }} />
                            </IconButton>
                          </Box>
                        )}

                        {/* File info */}
                        {imageUrl && !isDeleted && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, wordBreak: 'break-all' }}
                            >
                              {replacementFile
                                ? replacementFile.name
                                : fileName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatBytes(
                                replacementFile
                                  ? replacementFile.size
                                  : fileSize,
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )
                  })
                )}
              </Box>
            )}
          </Stack>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: '1px solid #ECEFF7',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Delete button - only show in edit mode */}
          {isEditMode && onDelete ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                onDelete()
                onClose()
              }}
              disabled={saving}
            >
              {t('Delete')}
            </Button>
          ) : (
            <Box /> // Spacer
          )}

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleClose} disabled={saving}>
              {t('Cancel')}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving
                ? isEditMode
                  ? t('Updating...')
                  : t('Uploading...')
                : isEditMode
                  ? t('Update Image')
                  : t('Upload Image')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  )
}

export default ImageDrawer
